import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateMany } from "../../../../../api/onboarding";
import { GeaoContext } from "../../../GeaoContextProvider";
import { PageStatus } from "../../../hooks/useInterviewUpdater";
import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { formDataToQuestionAnswers } from "./mappers";

/**
 * Custom React hook for handling form events in the Principal flow W9 Tax Form page.
 * Manages form submission, save and exit functionality, and API request states.
 * 
 * @param {Function} [getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [trigger=() => Promise.resolve()] - Function to trigger form validation
 * @returns {Object} Object containing form event handlers and loading state
 * @returns {Function} returns.onSubmit - Handler for form submission that navigates to next page
 * @returns {Function} returns.onSaveAndExit - Handler for saving progress and returning to checklist
 * @returns {boolean} returns.saving - Boolean indicating if an API request is in progress
 */
export default function useFormEvents(
    getValues = () => ({}),
    trigger = () => { return Promise.resolve(); }
) {
    const navigate = useNavigate();

    const {
        agencyInterview,
        setAgencyInterview,
        agentInterview,
        setAgentInterview
    } = useContext(GeaoContext);

    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);

    const saving = updateStatus === ApiRequestStatus.Pending;

    /**
     * Effect hook that handles navigation after successful API updates.
     * Navigates to the specified next path after successful update.
     */
    useEffect(() => {
        if (updateStatus === ApiRequestStatus.Success) {
            navigate(nextPath);
        }
    }, [updateStatus, nextPath, navigate]);

    /**
     * Handles form submission and proceeds to the next step in the principal flow.
     * Sets the next path to Principal Errors and Omissions and updates the interview.
     * 
     * @param {Object} formData - The form data to be submitted
     */
    const onSubmit = (formData) => {
        setNextPath(GeaoPaths.PrincipalErrorsAndOmissions);
        updateInterview(formData, false);
    };

    /**
     * Handles the save and exit action.
     * Triggers form validation, then saves current form values and navigates back to checklist.
     */
    const onSaveAndExit = () => {
        trigger().then(validationPassed => {
            if (validationPassed) {
                setNextPath(GeaoPaths.CreateYourProfileChecklist);
                updateInterview(getValues(), true);
            }
        });
    };

    /**
     * Updates both agent and agency interviews with form data via a single API call.
     * Manages API request state and handles success/error scenarios.
     * 
     * @param {Object} formData - The form data to be saved
     * @param {boolean} isSaveAndExit - Whether this is a save and exit operation
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus === ApiRequestStatus.Pending) return;

        // Create activity request for save and exit operations
        const activityRequest = isSaveAndExit ? {
            ActivityCategory: "GEAO_ONBOARDING",
            ActivitySubType: "SAVE_AND_EXIT",
            SourceApplication: "AGIWUI",
            AdditionalData: {
                ComponentName: "PrincipalW9TaxForm"
            }
        } : null;

        const updatedAgentInterview = {
            ...agentInterview,
            pageStatus: {
                ...agentInterview?.pageStatus,
                "PrincipalW9TaxForm": PageStatus.Completed
            }
        };

        const updateAgentInterviewRequest = {
            identityType: "Agent",
            interview: updatedAgentInterview,
            activityRequest: activityRequest
        };

        const updatedAgencyInterview = {
            ...agencyInterview,
            questionAnswers: {
                ...agencyInterview?.questionAnswers,
                ...formDataToQuestionAnswers(formData)
            },
            pageStatus: {
                ...agencyInterview?.pageStatus,
                "PrincipalW9TaxForm": PageStatus.Completed
            }
        };

        const updateAgencyInterviewRequest = {
            identityType: "Agency",
            interview: updatedAgencyInterview
        };

        setUpdateStatus(ApiRequestStatus.Pending);

        updateMany(
            [updateAgencyInterviewRequest, updateAgentInterviewRequest],
            (responseInterviews) => {
                // Success callback
                setAgencyInterview(responseInterviews.find(i => i.interviewId === agencyInterview.interviewId));
                setAgentInterview(responseInterviews.find(i => i.interviewId === agentInterview.interviewId));
                setUpdateStatus(ApiRequestStatus.Success);
            },
            () => {
                // Finally callback
            },
            () => {
                // Error callback
                setUpdateStatus(ApiRequestStatus.Error);
            }
        );
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving
    };
}
